import { type JSX, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { ActionsApi } from 'Api/Actions/ActionsApi';
import { Link } from 'Components/Buttons/Buttons';
import { CountLinkButton } from 'Components/Buttons/CountLinkButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { ACTION_DETAILS, CREATE_ACTION } from 'Config/PathTemplates';
import { Action, ActionStatus, actionStatusComparator } from 'Models/Actions';
import { MappedRisksModal, MappedRisksModalProps } from 'Pages/ActionPlanning/ActionsListing/MappedRisksModal/MappedRisksModal';

export interface ActionsListingProps {
    actionsApi: ActionsApi;
}

export const ActionsListing = (props: ActionsListingProps): JSX.Element => {
    const cachedData = useCachedData();

    const [actions, setActions] = useState<Action[]>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [selectedAction, setSelectedAction] = useState<Action>();

    useEffect(() => {
        const getActions = async (): Promise<void> => {
            try {
                const actionsResponse = await props.actionsApi.getAllActions();
                setActions(actionsResponse.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getActions();
    }, [props.actionsApi]);

    const showModal = (action: Action) => {
        setSelectedAction(action);
    };

    const columns: ColumnConfig<Action>[] = [
        {
            type: ColumnType.STRING,
            name: 'Title',
            getString: (action) => action.title,
            getOnClickTextAction: (action) => ({ goToLocation: generatePath(ACTION_DETAILS, { action_id: action.id }) }),
        },
        {
            type: ColumnType.ENUM,
            name: 'Status',
            getString: (action) => action.status,
            sortComparator: (a, b) => actionStatusComparator(a.status, b.status),
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (action) => action.owner,
            users: cachedData.users,
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Risks',
            getTableCellContent: (action) => {
                const count = action.associated_risks.length;
                if (count === 0) {
                    return <></>;
                }
                return <CountLinkButton count={count} noun="risk" onClick={() => showModal(action)} />;
            },
            getValue: (action) => action.associated_risks.length,
            sortComparator: (a, b) => a - b,
        },
        {
            type: ColumnType.DATE,
            name: 'Due Date',
            getDateString: (action) => action.due_date,
        },
        {
            type: ColumnType.MULTIPLE_TAGS,
            name: 'Tags',
            getTagIds: (action) => action.tags,
            convertTagIdsToDisplayableTags: cachedData.convertTagIdsToDisplayableTags,
        },
    ];

    const filters: FilterConfig<Action>[] = [
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'statusFilter',
            label: 'Filter by Status',
            getFilterableDataForRow: (action) => [action.status],
            options: [
                { name: 'Open', value: ActionStatus.OPEN },
                { name: 'Closed', value: ActionStatus.CLOSED },
            ],
            defaultSelections: [ActionStatus.OPEN],
        },
        {
            type: FilterType.USERS,
            id: 'ownerFilter',
            label: 'Filter by Owner',
            getUserIdsForRow: (action) => [action.owner],
        },
        {
            type: FilterType.TAGS,
            id: 'tagsFilter',
            label: 'Filter by Tags',
            getTagIdsForRow: (action) => action.tags,
        },
    ];

    const mappedRisksModalProps: MappedRisksModalProps | undefined =
        actions && selectedAction !== undefined
            ? {
                  hideModal: () => setSelectedAction(undefined),
                  associatedRisks: selectedAction.associated_risks,
              }
            : undefined;

    if (actions) {
        return (
            <>
                {mappedRisksModalProps && <MappedRisksModal {...mappedRisksModalProps} />}
                <PageLayoutDefault
                    headerTitle="Action Planning"
                    headerButtons={
                        <Link variant="primaryButton" to={CREATE_ACTION} fontAwesomeImage={ICON_ADD_CREATE}>
                            Create Action
                        </Link>
                    }
                    body={[
                        {
                            content: <Table<Action> columns={columns} defaultSortColumn="Title" filters={filters} getUniqueIdForRow={(action) => action.id} rowsData={actions} />,
                        },
                    ]}
                />
            </>
        );
    } else if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    } else {
        return <Placeholder />;
    }
};
